import { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { HeadSection } from 'src/components/elements'
import { Button, InputField, MyModal, TextAreaField } from 'src/components/ui'
import { useSubmitInquiryMutation } from 'src/redux/api'

interface IGuestInquiryModal {
  open: boolean
  handleClose: () => void
  propertyId: string
  propertyTitle?: string
}

interface FormValues {
  name: string
  email: string
  phone: string
  message: string
}

const emailPattern = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/

// Lets a logged-out visitor on a public listing page request a viewing /
// contact the owner. Lands in the admin "Запити незареєстрованих" inbox.
const GuestInquiryModal: FC<IGuestInquiryModal> = ({
  open,
  handleClose,
  propertyId,
  propertyTitle,
}) => {
  const { t } = useTranslation()
  const [submitInquiry, { isLoading }] = useSubmitInquiryMutation()
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { name: '', email: '', phone: '', message: '' },
  })

  const onSubmit = async (data: FormValues) => {
    try {
      await submitInquiry({
        name: data.name,
        email: data.email,
        phone: data.phone || undefined,
        subject: propertyTitle
          ? t('guest_inquiry.subject_with_title', { title: propertyTitle })
          : t('guest_inquiry.subject'),
        message: data.message,
        requestType: 'VIEWING_REQUEST',
        propertyId,
      }).unwrap()
      toast.success(t('guest_inquiry.success'))
      reset()
      handleClose()
    } catch {
      // surfaced by the global api toast middleware
    }
  }

  return (
    <MyModal open={open} handleClose={handleClose} width="w-[calc(100%_-_2rem)] md:w-[32rem]">
      <div className="p-5 flex flex-col gap-4 md:min-w-[26rem]">
        <HeadSection title={t('guest_inquiry.title')} />
        <p className="text-sm text-ink-2">{t('guest_inquiry.hint')}</p>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <Controller
            name="name"
            control={control}
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <InputField
                {...field}
                variant="accented"
                placeholder={t('guest_inquiry.name')}
                autoComplete="name"
                error={errors.name}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: t('validation.required'),
              pattern: { value: emailPattern, message: t('login.emailOrPhoneError') },
            }}
            render={({ field }) => (
              <InputField
                {...field}
                variant="accented"
                placeholder={t('guest_inquiry.email')}
                type="email"
                autoComplete="email"
                error={errors.email}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <InputField
                {...field}
                variant="accented"
                placeholder={t('guest_inquiry.phone')}
                type="tel"
                autoComplete="tel"
                error={errors.phone}
              />
            )}
          />
          <Controller
            name="message"
            control={control}
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <TextAreaField
                {...field}
                placeholder={t('guest_inquiry.message')}
                error={errors.message?.message}
              />
            )}
          />
          <Button variant="large" type="submit" isLoading={isLoading}>
            {t('guest_inquiry.submit')}
          </Button>
        </form>
      </div>
    </MyModal>
  )
}

export default GuestInquiryModal
